import type { Dispatch } from 'react';
import type { StoreApi } from 'zustand';

import { getTimeframeFromCalendarSelection } from './calendar.js';
import {
  applySetToNow,
  closeCalendarAndFocusInput,
  handleClearCalendarValue,
} from './helpers.js';
import type { FormFieldMessagesReducerAction } from '../../../../forms/forms-core/state/formfield-messages-state-reducer.js';
import { sortTimeframeValues } from '../../../../forms/forms-core/time/helpers.js';
import { removePrecisionFromTimeFrame } from '../../../../forms/forms-core/time/timeParser.js';
import { type Timeframe } from '../../../types/time.js';
import {
  isFullTimeframe,
  isNowTimeValue,
  isTimeValue,
  mergeRangeValue,
} from '../../helpers.js';
import { validate, isOutOfBounds } from '../../validation.js';
import type {
  DateTimeRangePickerTimeframeStates,
  DateTimeRangePickerTimeframeStoreActions,
} from '../dateTimeRangePicker.types.js';

/**
 * Creates actions for the DateTimeRangePicker store using the slice action handlers.
 *  @internal
 */
export function getDateTimeRangePickerActions(
  isControlled: boolean,
  formFieldMessagesDispatch?: Dispatch<FormFieldMessagesReducerAction>,
  onChangeProp?: ((value: Timeframe | null) => void) | undefined,
) {
  const actions = (
    set: StoreApi<
      DateTimeRangePickerTimeframeStates &
        DateTimeRangePickerTimeframeStoreActions
    >['setState'],
  ): DateTimeRangePickerTimeframeStoreActions => {
    return {
      setCalendarValue: (inputValue) => {
        set((state) => {
          // TimeValue input (e.g. a "now" expression): apply it as the "to" boundary directly.
          if (isTimeValue(inputValue) && inputValue.value === 'now()') {
            const update = applySetToNow(state, {
              isControlled,
              onChangeProp,
              formFieldMessagesDispatch,
              validateMode: 'dateTimePicker',
            });
            // nowInserted: true only in the "pending" case — button clicked with no existing
            // from, so calendar stays open waiting for the complementary boundary.
            const nowInserted =
              'dateTimeRangePickerTimeframe' in update &&
              !update.dateTimeRangePickerTimeframe?.from &&
              isNowTimeValue(update.dateTimeRangePickerTimeframe?.to);
            return { ...update, nowInserted };
          }

          const calendarTimeframe = getTimeframeFromCalendarSelection(
            inputValue,
            state,
          );

          const nowIsOutOfBounds =
            isOutOfBounds(
              'now',
              isNowTimeValue(state.min) ? null : state.min,
              isNowTimeValue(state.max) ? null : state.max,
            ) !== undefined;

          // The user set `to = now` (via button or typing) without a `from` yet.
          // When they click a calendar date, pair it with the existing `to = now`.
          if (
            !nowIsOutOfBounds &&
            !state.dateTimeRangePickerTimeframe?.from &&
            state.dateTimeRangePickerTimeframe?.to &&
            isNowTimeValue(state.dateTimeRangePickerTimeframe.to) &&
            calendarTimeframe?.from &&
            !calendarTimeframe?.to
          ) {
            const timeframe = sortTimeframeValues(
              {
                from: calendarTimeframe.from,
                to: state.dateTimeRangePickerTimeframe.to,
              },
              state.min,
              state.max,
              state.precision,
            );

            if (isFullTimeframe(timeframe)) {
              closeCalendarAndFocusInput(
                state.dateTimeRangePickerRef,
                state.dateTimeFromRef,
              );
              onChangeProp?.(timeframe);
            }

            return {
              calendarValue: timeframe,
              dateTimeRangePickerTimeframe: timeframe,
              nowInserted: false,
            };
          }

          if (isFullTimeframe(calendarTimeframe)) {
            closeCalendarAndFocusInput(
              state.dateTimeRangePickerRef,
              state.dateTimeFromRef,
            );
            onChangeProp?.(calendarTimeframe);

            return {
              calendarValue: calendarTimeframe,
              dateTimeRangePickerTimeframe: calendarTimeframe,
              nowInserted: false,
            };
          }

          return { calendarValue: calendarTimeframe, nowInserted: false };
        });
      },
      setDateTimeRangePickerTimeframe: (value) => {
        return set((state) => {
          // Full clear: null resets both fields to null
          if (value === null) {
            onChangeProp?.(null);
            return {
              dateTimeRangePickerTimeframe: null,
              calendarValue: null,
              nowInserted: false,
            };
          }

          const merged = mergeRangeValue(
            state.dateTimeRangePickerTimeframe,
            value,
          );

          const timeframeWithPrecision =
            removePrecisionFromTimeFrame(merged, state.precision) ?? null;

          if (
            timeframeWithPrecision?.from === null &&
            timeframeWithPrecision?.to === null
          ) {
            onChangeProp?.(null);
          }

          isFullTimeframe(timeframeWithPrecision) &&
            onChangeProp?.(timeframeWithPrecision);

          const isPartialFrom =
            isTimeValue(merged.from) && merged.from.absoluteDate === '';
          const isPartialTo =
            isTimeValue(merged.to) && merged.to.absoluteDate === '';

          // Partial values while editing
          if (isPartialFrom || isPartialTo) {
            const errorKey = validate(
              timeframeWithPrecision,
              state.min,
              state.max,
              {
                formMessageDispatch: formFieldMessagesDispatch,
                formerError: state.dateTimeRangePickerError,
                precision: state.precision,
                showError: false,
                checkOrder: true,
                ref: state.dateTimeRangePickerRef,
                expectedFormat: state.formatString,
                valueMode: 'dateTimePicker',
                required: state.required,
              },
            );

            const calendarValue = {
              from: isPartialFrom ? null : timeframeWithPrecision?.from ?? null,
              to: isPartialTo ? null : timeframeWithPrecision?.to ?? null,
            };

            return {
              dateTimeRangePickerTimeframe: timeframeWithPrecision,
              calendarValue,
              calendarError: errorKey,
              dateTimeRangePickerError: errorKey,
            };
          }

          const dateTimeRangePickerError = validate(
            timeframeWithPrecision,
            state.min,
            state.max,
            {
              formMessageDispatch: formFieldMessagesDispatch,
              formerError: state.dateTimeRangePickerError,
              precision: state.precision,
              showError: false,
              checkOrder: true,
              ref: state.dateTimeRangePickerRef,
              expectedFormat: state.formatString,
              valueMode: 'dateTimePicker',
              required: state.required,
            },
          );

          return {
            dateTimeRangePickerTimeframe: timeframeWithPrecision,
            calendarValue: timeframeWithPrecision,
            calendarError: dateTimeRangePickerError,
            dateTimeRangePickerError,
          };
        });
      },
      clearCalendarValue: () => {
        set((state) => handleClearCalendarValue(state));
      },
    };
  };

  return actions;
}
